import * as PIXI from "pixi.js";
import { CommonOperatorEvent } from "../../../api/common/operator/CommonOperatorEvent";
import { Logger } from "../../../util/log/Logger";
import { AppEvent } from "../../Constants";
import { MouseBlocker } from "../../control/MouseBlocker";
import { TitanButton } from "../../control/TitanButton";
import { TitanPanel } from "../../control/TitanPanel";
import { TitanSprite } from "../../control/TitanSprite";
import { MouseBlockerProperties } from "../../control/properties/MouseBlockerProperties";
import { CommonGameEvent } from "../../controller/event/CommonGameEvent";
import { AbstractView } from "../AbstractView";
import { CommonStyle } from "../style/CommonStyle";
/**
 * AbstractOptionsMenu.ts
 */
export class AbstractOptionsMenu extends AbstractView {
    constructor(factory, properties, portraitProperties) {
        super(properties, portraitProperties);
        this.factory = factory;
        this.operatorConfig = this.factory.getOperatorConfig();
        this.model = this.factory.getModel();
        this.run();
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        super.create();
        this.createOptions();
        if (!this.operatorConfig.isOptionsEnabled()) {
            this.visible = false;
        }
        Logger.addObjectRef(this, "OptionsMenu");
    }
    createOptions() {
        this.createOptionsButton();
        this.createMouseBlocker();
        this.createOptionsHolder();
        this.createOptionsPanel();
        this.createButtons();
        this.createOptionsMenu();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(CommonOperatorEvent.HELP_OPEN, this.handleHelpButtonSelected);
        this.registerCore(CommonOperatorEvent.SOUND_ON, this.handleOperatorSoundOn);
        this.registerCore(CommonOperatorEvent.SOUND_OFF, this.handleOperatorSoundOff);
        this.registerCore(AppEvent.IDLE, this.handleIdle);
        this.registerCore(AppEvent.BUSY, this.handleBusy);
        this.registerGame(CommonGameEvent.START_GAME, this.handleStartGame);
        this.registerGame(CommonGameEvent.COMPLETE, this.handleComplete);
        this.registerGame(CommonGameEvent.RECOVERY, this.handleRecovery);
        this.registerGame(CommonGameEvent.REPLAY_FINISHED, this.handleComplete);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_IN, this.freeRoundsIn);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_NEW_GAME_REQUEST, this.freeRoundsOut);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_OUT, this.freeRoundsOut);
        this.registerGame(CommonGameEvent.FREE_ROUNDS_EXTRA_SPINS, this.freeRoundsOut);
    }
    createMouseBlocker() {
        const properties = new MouseBlockerProperties(this.landscapeProperties.backgroundColour || 0x000000, this.landscapeProperties.backgroundAlpha || 0.2, true);
        this.mouseBlocker = new MouseBlocker(properties);
        this.mouseBlocker.onClick(() => {
            this.model.dispatchKPI("TITAN_STATE", "OPTIONS_MENU_CANCEL", null);
            this.handleOptionsButtonSelected();
        }, this);
    }
    createOptionsButton() {
        this.optionsButton = new TitanButton(this.landscapeProperties.optionsButtonProperties, this.portraitProperties?.optionsButtonProperties);
        this.optionsButton.name = "optionsButton";
        this.optionsButton.onClick(this.handleOptionsButtonSelected, this);
        this.addChild(this.optionsButton);
    }
    createOptionsHolder() {
        this.optionsHolder = new PIXI.Container();
    }
    createOptionsPanel() {
        this.panel = new TitanPanel(this.landscapeProperties.panelProperties, this.portraitProperties?.panelProperties);
    }
    createButtons() {
        this.createDepositButton();
        this.createSFXButton();
        this.createBackgroundSoundButton();
        this.createPayTableButton();
        this.createResponsibleGamingButton();
        this.createHelpButton();
        this.createHomeButton();
        this.createDGHButton();
    }
    createDepositButton() {
        if (this.operatorConfig.isDepositButtonEnabled()) {
            this.depositButton = this.createButton("depositButton", this.landscapeProperties.depositButtonProperties, this.portraitProperties?.depositButtonProperties, this.handleDepositButtonSelected);
        }
    }
    createHomeButton() {
        if (this.operatorConfig.isHomeButtonEnabled()) {
            this.homeButton = this.createButton("homeButton", this.landscapeProperties.homeButtonProperties, this.portraitProperties?.homeButtonProperties, this.handleHomeButtonSelected);
        }
    }
    createSFXButton() {
        this.sfxButton = this.createButton("sfxButton", this.landscapeProperties.SFXButtonProperties, this.portraitProperties?.SFXButtonProperties, this.handleSFXButtonSelected);
    }
    createBackgroundSoundButton() {
        this.backgroundSoundButton = this.createButton("backgroundSoundButton", this.landscapeProperties.backgroundSoundButtonProperties, this.portraitProperties?.backgroundSoundButtonProperties, this.handleBackgroundSoundButtonSelected);
    }
    createHelpButton() {
        if (this.operatorConfig.isHelpButtonEnabled()) {
            this.helpButton = this.createButton("helpButton", this.landscapeProperties.helpButtonProperties, this.portraitProperties?.helpButtonProperties, this.handleHelpButtonSelected);
        }
    }
    createPayTableButton() {
        if (this.uiProperties.hasPayTable) {
            this.payTableButton = this.createButton("payTableButton", this.landscapeProperties.payTableButtonProperties, this.portraitProperties?.payTableButtonProperties, this.handlePayTableButtonSelected);
        }
    }
    createResponsibleGamingButton() {
        let responsibleGaming = this.model.getResponsibleGaming();
        if (responsibleGaming.isEnabled() && this.uiProperties.responsibleGamingButtonProperties) {
            this.responsibleGamingButton = this.createButton("responsibleGamingButton", this.landscapeProperties.responsibleGamingButtonProperties, this.portraitProperties?.responsibleGamingButtonProperties, this.handleResponsibleGamingButtonSelected);
        }
    }
    createDGHButton() {
        if (this.operatorConfig.isDetailedGameHistoryEnabled()) {
            this.dghButton = this.createButton("dghButton", this.landscapeProperties.dghButtonProperties, this.portraitProperties?.dghButtonProperties, this.handleDGHButtonSelected);
        }
    }
    createButton(btnName, properties, portraitProperties, handler) {
        let button;
        if (properties) {
            button = new TitanButton(properties, portraitProperties);
            button.name = btnName;
            button.onClick(handler, this);
            this.panel.addToPanel(button, false);
        }
        return button;
    }
    createOptionsMenu() {
        let size = this.panel.getTotalChildren();
        if (this.uiProperties.optionsMenuProperties.spriteSheet) {
            this.landscapeProperties.optionsMenuProperties.id = CommonStyle.OPTIONS_MENU + size;
            if (this.portraitProperties) {
                this.portraitProperties.optionsMenuProperties.id = CommonStyle.OPTIONS_MENU + size;
            }
            this.optionsMenu = new TitanSprite(this.landscapeProperties.optionsMenuProperties, this.portraitProperties?.optionsMenuProperties);
            if (!this.optionsMenu) {
                Logger.forceLog("Missing options menu graphic for " + size + " menus");
                this.optionsMenu = new PIXI.Container();
                this.optionsMenu.position.copyFrom(this.uiProperties.optionsMenuProperties.position);
            }
        }
        else {
            this.optionsMenu = new PIXI.Container();
            this.optionsMenu.position.copyFrom(this.uiProperties.optionsMenuProperties.position);
        }
        this.optionsMenu.name = "optionsMenu";
        this.optionsMenu.addChild(this.panel);
        this.panel.arrange();
    }
    enable() {
        this.optionsButton.setEnabled(true);
    }
    disable() {
        this.optionsButton.setEnabled(false);
    }
    handleHomeButtonSelected() {
        this.hide();
        this.model.dispatchKPI("TITAN_STATE", "OPTIONS_MENU_EXIT_GAME", null);
        this.dispatchCore(AppEvent.EXIT_GAME);
    }
    handleDepositButtonSelected() {
        this.hide();
        this.dispatchCore(AppEvent.DEPOSIT);
    }
    handleSFXButtonSelected() {
        let event = this.sfxButton.isSelected() ? CommonGameEvent.SFX_OFF : CommonGameEvent.SFX_ON;
        this.model.dispatchKPI("TITAN_STATE", this.sfxButton.isSelected() ? "GAME_SOUND_EFFECTS_OFF_INTERNAL" : "GAME_SOUND_EFFECTS_ON_INTERNAL", null);
        this.model.dispatch(event);
    }
    handleBackgroundSoundButtonSelected() {
        let event = this.backgroundSoundButton.isSelected() ? CommonGameEvent.BACKGROUND_SOUND_OFF : CommonGameEvent.BACKGROUND_SOUND_ON;
        this.model.dispatchKPI("TITAN_STATE", this.backgroundSoundButton.isSelected() ? "GAME_MUSIC_OFF_INTERNAL" : "GAME_MUSIC_ON_INTERNAL", null);
        this.model.dispatch(event);
    }
    handleHelpButtonSelected() {
        this.hide();
        this.dispatchGame(CommonGameEvent.HELP_DIALOG_OPEN);
        this.model.dispatch(CommonGameEvent.HELP_OPEN);
    }
    handlePayTableButtonSelected() {
        this.hide();
        this.model.dispatch(CommonGameEvent.PAY_TABLE_OPEN);
    }
    handleResponsibleGamingButtonSelected() {
        this.hide();
        this.model.dispatch(CommonGameEvent.RESPONSIBLE_GAMING_OPEN);
    }
    handleDGHButtonSelected() {
        this.hide();
        console.log("DGH button selected in OM");
        this.core.dispatchEvent(CommonGameEvent.DGH_OPEN);
    }
    handleOptionsButtonSelected() {
        this.isOpen() ? this.hide() : this.show();
    }
    handleOperatorSoundOff() {
        if (this.sfxButton) {
            this.model.dispatchKPI("TITAN_STATE", "GAME_SOUND_EFFECTS_OFF_EXTERNAL", null);
            this.sfxButton.setSelected(true);
            this.handleSFXButtonSelected();
        }
        if (this.backgroundSoundButton) {
            this.model.dispatchKPI("TITAN_STATE", "GAME_SOUND_MUSIC_OFF_EXTERNAL", null);
            this.backgroundSoundButton.setSelected(true);
            this.handleBackgroundSoundButtonSelected();
        }
    }
    handleBusy() {
        this.disable();
    }
    handleIdle() {
        this.enable();
    }
    handleStartGame() {
        TitanButton.disableButton(this.responsibleGamingButton);
        TitanButton.disableButton(this.dghButton);
    }
    handleComplete() {
        TitanButton.enableButton(this.responsibleGamingButton);
        TitanButton.enableButton(this.dghButton);
    }
    handleRecovery() {
        TitanButton.disableButton(this.responsibleGamingButton);
        TitanButton.disableButton(this.dghButton);
    }
    show() {
        this.renderer.getHolder().addChild(this.optionsHolder);
        this.optionsHolder.addChild(this.mouseBlocker);
        this.optionsHolder.addChild(this.optionsMenu);
        this.model.dispatchKPI("TITAN_STATE", "OPTIONS_MENU_OPEN", null);
        this.dispatchCore(CommonGameEvent.OPTIONS_MENU_OPEN);
        this.optionsButton.setSelected(true);
    }
    handleOperatorSoundOn() {
        if (this.sfxButton) {
            this.model.dispatchKPI("TITAN_STATE", "GAME_SOUND_EFFECTS_ON_EXTERNAL", null);
            this.sfxButton.setSelected(false);
            this.handleSFXButtonSelected();
        }
        if (this.backgroundSoundButton) {
            this.model.dispatchKPI("TITAN_STATE", "GAME_MUSIC_ON_EXTERNAL", null);
            this.backgroundSoundButton.setSelected(false);
            this.handleBackgroundSoundButtonSelected();
        }
    }
    hide() {
        this.unhighlightButtons();
        this.renderer.removeChild(this.optionsHolder);
        this.optionsHolder.removeChild(this.mouseBlocker);
        this.optionsHolder.removeChild(this.optionsMenu);
        this.dispatchCore(CommonGameEvent.OPTIONS_MENU_CLOSE);
    }
    unhighlightButtons() {
        super.unhighlightButtons();
        this.optionsMenu?.children?.forEach((child) => {
            if (child.unhighlightButtons instanceof Function) {
                child.unhighlightButtons();
            }
        });
    }
    isOpen() {
        return this.optionsHolder.children.indexOf(this.optionsMenu) >= 0;
    }
    dispatchGame(event) {
        this.model.dispatch(event);
    }
    registerGame(state, handler) {
        this.model.register(state, handler, this);
    }
    handleResize() {
        if (!this.renderer.getRotated()) {
            let transform = this.renderer.getSafeTransform();
            this.y = this.uiProperties.position.y - transform.y;
            if (this.uiProperties.responsiveLayout) {
                let xDiff = transform.x;
                this.optionsButton.x = this.uiProperties.optionsButtonProperties.position.x + xDiff;
                this.optionsMenu.x = this.uiProperties.optionsMenuProperties.position.x + xDiff;
            }
        }
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
        this.optionsMenu.position.copyFrom(this.uiProperties.optionsMenuProperties.position);
    }
    freeRoundsIn() {
        if (this.responsibleGamingButton) {
            this.responsibleGamingButton.setEnabled(false);
        }
    }
    freeRoundsOut() {
        if (this.responsibleGamingButton) {
            this.responsibleGamingButton.setEnabled(true);
        }
        this.optionsButton?.setEnabled(true);
    }
}
//# sourceMappingURL=AbstractOptionsMenu.js.map